import {Alert, Stack, Textarea} from '@mantine/core'
import {type FormErrors, useForm} from '@mantine/form'
import {notifications} from '@mantine/notifications'
import {useNavigate} from '@tanstack/react-router'
import {useEffect, useState} from 'react'

import {useRentalAssetTypes} from 'src/assets/rental-assets/use-rental-asset-types'
import {useWorkerTypes} from 'src/assets/worker-types/use-worker-types'
import {useCurrentUser} from 'src/auth/use-current-user'
import {useDispatchYards} from 'src/dispatch/use-dispatch'
import {clearCreatedMark, clearDraft, loadCreatedMark, saveDraft} from 'src/jobs/create-job-storage'
import {billsEachLine} from 'src/jobs/order-billing'
import type {CreateJobFormValues} from 'src/jobs/types'
import type {ProjectBillingMethod} from 'src/projects/schemas'
import {useProjectBillingTerms} from 'src/projects/use-projects'
import {useQuote} from 'src/quotes/use-quotes'
import {useActiveServiceTypes} from 'src/settings/use-service-types'
import {useTransportationRatePolicy} from 'src/settings/use-transportation-rate-policy'

import {BillingSection} from './billing-section'
import {CrewEquipmentSection} from './crew-equipment-section'
import {CustomerSiteSection} from './customer-site-section'
import {DraftNotice} from './draft-notice'
import {
    addLine,
    type AddToOrder,
    fillLastRates,
    forPreviousCustomer,
    JOB_ENTRY_SECTIONS,
    type JobEntrySectionId,
    linesBilled,
    nearestYardId,
    orderBillingMethod,
    prefillFromJob,
    requiresPlannedEnd,
    sectionOf,
    suggestCategory,
    validateJobEntry,
    withPageDefaults,
} from './job-entry-logic'
import {JobEntrySection} from './job-entry-section'
import {SaveBar} from './save-bar'
import {useCustomerLastRates} from './use-customer-last-rates'
import {type JobEntryStart, useFetchJob} from './use-job-entry-prefill'
import {useSaveJobEntry} from './use-save-job-entry'
import {WhatWhenSection} from './what-when-section'

type Lines = Pick<CreateJobFormValues, 'workerRequirements' | 'assetRequirements'>

const COPY_FAILED = {
    'source-job': {title: 'Could not load source job', message: 'Starting with an empty form.'},
    'clone-job': {title: 'Could not load job', message: 'Try again.'},
}

type JobEntryFormProps = {
    start: JobEntryStart
    storageKey: string
    quoteId: string | null
    order: AddToOrder | null
    readOnly: boolean
    onStartOver: () => void
}

function selectTextFormErrors(errors: FormErrors): Record<string, string> {
    return Object.fromEntries(
        Object.entries(errors).filter(
            (entry): entry is [string, string] => typeof entry[1] === 'string',
        ),
    )
}

export function JobEntryForm({
    start,
    storageKey,
    quoteId,
    order,
    readOnly,
    onStartOver,
}: JobEntryFormProps) {
    const navigate = useNavigate()
    const currentUser = useCurrentUser()
    const fetchJob = useFetchJob()
    const saveJob = useSaveJobEntry({storageKey, order})
    const {data: workerTypes = []} = useWorkerTypes()
    const {data: assetTypes = []} = useRentalAssetTypes()
    const {data: categories = []} = useActiveServiceTypes()
    const {data: yards = []} = useDispatchYards()
    const {data: ratePolicy} = useTransportationRatePolicy()
    const {data: quote} = useQuote(quoteId ?? '', {enabled: Boolean(quoteId)})
    const {data: orderTerms = []} = useProjectBillingTerms(order?.projectId ?? '', {
        enabled: order != null,
    })
    const [customerName, setCustomerName] = useState(start.customerName)
    const [createdMark] = useState(() => loadCreatedMark(storageKey))
    const [saveAttempts, setSaveAttempts] = useState(0)
    const [ratesCleared, setRatesCleared] = useState(start.ratesCleared)

    const form = useForm<CreateJobFormValues>({
        initialValues: start.values,
        validateInputOnBlur: true,
        clearInputErrorOnChange: false,
        validate: (raw): Record<string, string> => {
            const values = resolveValuesWithPageDefaults(raw).values
            return validateJobEntry(values, {
                needsPlannedEnd: requiresPlannedEnd(values, assetTypes, quote),
                activeMinimumHourlyRate: ratePolicy?.minimumHourlyRate ?? null,
                addToOrder: order != null,
                orderMethod,
            })
        },
        onValuesChange: (values) => {
            clearCreatedMark(storageKey)
            saveDraft(values, customerName, storageKey)
            if (saveAttempts > 0) {
                form.validate()
            }
        },
    })
    const {data: lastRates = []} = useCustomerLastRates(form.values.customerId)
    const orderMethod: ProjectBillingMethod | undefined = order
        ? orderBillingMethod(orderTerms, form.values.projectBillingTermsId)
        : undefined

    function resolveValuesWithPageDefaults(
        values: CreateJobFormValues,
    ): ReturnType<typeof withPageDefaults> {
        return withPageDefaults(values, {
            suggestedYardId:
                nearestYardId(yards, values.locations[0]) ?? currentUser?.dispatchYardId ?? null,
            lastRates,
            assetTypes,
            orderMethod,
        })
    }
    const resolved = resolveValuesWithPageDefaults(form.values)
    const values = resolved.values
    const errors = selectTextFormErrors(form.errors)
    const errorCount = (section: JobEntrySectionId) =>
        Object.keys(errors).filter((key) => sectionOf(key) === section).length

    useEffect(() => {
        if (start.copyFailed) {
            notifications.show({
                id: start.copyFailed,
                color: 'red',
                ...COPY_FAILED[start.copyFailed],
            })
        }
    }, [start.copyFailed])

    function setLines(lines: Lines) {
        const current = form.getValues()
        const suggestion = current.categoryPickedByRep
            ? null
            : suggestCategory(
                  {
                      assetTypeIds: lines.assetRequirements.map((row) => row.rentalAssetTypeId),
                      workerTypeIds: lines.workerRequirements.map((row) => row.workerTypeId),
                  },
                  {assetTypes, workerTypes, categories},
              )
        form.setValues({
            ...lines,
            ...(suggestion
                ? {
                      serviceTypeId: suggestion.categoryId,
                      specificType: suggestion.specificType ?? '',
                      scheduling: {...current.scheduling, serviceTypeId: suggestion.categoryId},
                  }
                : {}),
        })
    }

    function changeBillingMethod(method: ProjectBillingMethod) {
        const billed = linesBilled(method, fillLastRates(form.getValues(), lastRates), assetTypes)
        if (billsEachLine(method)) {
            setRatesCleared(false)
        } else if (billed.cleared) {
            setRatesCleared(true)
        }
        form.setValues({billingMethod: method, ...billed.lines})
    }

    async function startFromJob(jobId: string | null) {
        form.setFieldValue('cloneJobId', jobId)
        if (!jobId) {
            return
        }

        try {
            const job = await fetchJob(jobId)
            const current = form.getValues()
            const prefill = prefillFromJob(job, current.scheduling)
            const billed = linesBilled(current.billingMethod, {...current, ...prefill}, assetTypes)
            if (billed.cleared) {
                setRatesCleared(true)
            }
            form.setValues({...prefill, ...billed.lines, cloneJobId: jobId})
        } catch {
            form.setFieldValue('cloneJobId', null)
            notifications.show({color: 'red', ...COPY_FAILED['clone-job']})
        }
    }

    function handleCustomerChange(customerId: string | null, name: string | null) {
        const current = form.getValues()
        if (customerId !== current.customerId) {
            form.setValues({customerId, ...forPreviousCustomer(current)})
        }
        setCustomerName(name)
        saveDraft(form.getValues(), name, storageKey)
    }

    function handleCancel() {
        clearDraft(storageKey)
        clearCreatedMark(storageKey)
        if (order) {
            void (order.sourceJobId
                ? navigate({to: '/jobs/$jobId', params: {jobId: order.sourceJobId}})
                : navigate({to: '/projects/$projectId', params: {projectId: order.projectId}}))
            return
        }
        void (quoteId
            ? navigate({to: '/quotes/$quoteId', params: {quoteId}})
            : navigate({to: '/jobs'}))
    }

    function handleSave() {
        setSaveAttempts((count) => count + 1)
        const result = form.validate()
        if (!result.hasErrors) {
            saveJob.save(resolveValuesWithPageDefaults(form.getValues()).values)
            return
        }
        const first = JOB_ENTRY_SECTIONS.find((section) =>
            Object.keys(result.errors).some((key) => sectionOf(key) === section.id),
        )
        document
            .getElementById(`job-entry-${first?.id ?? 'customer'}`)
            ?.scrollIntoView?.({behavior: 'smooth', block: 'start'})
    }

    return (
        <Stack gap="lg" pb={96}>
            <DraftNotice
                restored={start.restored}
                created={createdMark}
                onStartOver={onStartOver}
            />
            {order?.sourceJobId && !start.restored ? (
                <Alert color="blue" variant="light">
                    Copied from the source job for the next day. Saving adds it to order{' '}
                    {order.orderLabel}.
                </Alert>
            ) : null}
            <JobEntrySection
                id="customer"
                title="Customer & site"
                errorCount={errorCount('customer')}
            >
                <CustomerSiteSection
                    form={form}
                    values={values}
                    errors={errors}
                    customerName={customerName}
                    order={order}
                    fromQuote={quoteId != null}
                    yards={yards}
                    siteYardId={nearestYardId(yards, values.locations[0])}
                    onCustomerChange={handleCustomerChange}
                    onStartFromJob={(jobId) => void startFromJob(jobId)}
                />
            </JobEntrySection>
            <JobEntrySection id="what" title="What & when" errorCount={errorCount('what')}>
                <WhatWhenSection
                    form={form}
                    errors={errors}
                    categories={categories}
                    needsPlannedEnd={requiresPlannedEnd(values, assetTypes, quote)}
                    saveAttempts={saveAttempts}
                />
            </JobEntrySection>
            <JobEntrySection id="crew" title="Crew & equipment" errorCount={errorCount('crew')}>
                <CrewEquipmentSection
                    lines={values}
                    billingMethod={values.billingMethod}
                    workerTypes={workerTypes}
                    assetTypes={assetTypes}
                    errors={errors}
                    onAddLine={(pick) => setLines(addLine(values, pick))}
                    onLinesChange={setLines}
                />
            </JobEntrySection>
            <JobEntrySection id="billing" title="Billing" errorCount={errorCount('billing')}>
                <BillingSection
                    form={form}
                    order={order}
                    billingMethod={values.billingMethod}
                    orderMethod={orderMethod}
                    ratesCleared={order ? resolved.ratesCleared : ratesCleared}
                    onBillingMethodChange={changeBillingMethod}
                />
            </JobEntrySection>
            <JobEntrySection id="notes" title="Notes" errorCount={0}>
                <Textarea
                    label="Notes"
                    description="Gate codes, what to bring, who to call"
                    autosize
                    minRows={3}
                    {...form.getInputProps('notes')}
                    onChange={(event) => {
                        const notes = event.currentTarget.value
                        form.setValues({
                            notes,
                            scheduling: {...form.getValues().scheduling, serviceNotes: notes},
                        })
                    }}
                />
            </JobEntrySection>
            <SaveBar
                userName={currentUser?.name ?? null}
                errorCount={Object.keys(errors).length}
                saveError={saveJob.errorMessage}
                readyForDispatch={values.scheduling.markJobsReadyForDispatch ?? true}
                saving={saveJob.isPending}
                readOnly={readOnly}
                onReadyForDispatchChange={(ready) =>
                    form.setFieldValue('scheduling', {
                        ...form.getValues().scheduling,
                        markJobsReadyForDispatch: ready,
                    })
                }
                onCancel={handleCancel}
                onSave={handleSave}
            />
        </Stack>
    )
}
